import React from 'react';
import { tokens } from '../theme';

// Shared presentational pieces for the hand-built list pages. Those pages render
// raw divs with inline styles, so they never picked up the Material-UI theme —
// these give them the DESIGN.md look without rewriting each page's logic.

const STATUS_COLORS = {
    active: tokens.success,
    completed: tokens.success,
    accepted: tokens.success,
    published: tokens.success,
    'closed-resolved': tokens.success,
    resolved: tokens.success,

    pending: tokens.warning,
    negotiating: tokens.warning,
    draft: tokens.warning,
    open: tokens.warning,

    banned: tokens.danger,
    deleted: tokens.danger,
    cancelled: tokens.danger,
    failed: tokens.danger,
    'closed-unresolved': tokens.danger,

    suspended: tokens.danger,
    deactivated: tokens.danger,
    rejected: tokens.danger,
    'pending review': tokens.warning,
    'more info required': tokens.warning,
    approved: tokens.success,

    admin: tokens.violet,
};

// Colour encodes state, never decoration — see DESIGN.md section 2. Unknown
// statuses fall back to neutral rather than borrowing a meaning they don't have.
export const StatusPill = ({ status, children }) => {
    const label = String(children || status || '').trim();
    if (!label) return null;
    const color = STATUS_COLORS[label.toLowerCase()] || tokens.muted;
    return (
        <span className="sk-pill" style={{ color, backgroundColor: `${color}1f` }}>
            {label}
        </span>
    );
};

const initialsFor = name => String(name || '?').trim().split(/\s+/).slice(0, 2)
    .map(part => part.charAt(0).toUpperCase()).join('') || '?';

export const Avatar = ({ src, alt = '', square, size = 'md' }) => {
    const [failed, setFailed] = React.useState(false);
    const showImage = Boolean(src) && !failed;
    return (
        <div className={`sk-avatar${square ? ' sk-avatar-square' : ''} sk-avatar-${size}`} aria-label={`${alt || 'User'} avatar`} data-seed={initialsFor(alt)}>
            {showImage
                ? <img src={src} alt={alt || 'Profile'} onError={() => setFailed(true)} />
                : <span aria-hidden="true">{initialsFor(alt)}</span>}
        </div>
    );
};

export const Row = ({ children, right, href, className = '' }) => (
    <div className={`sk-row ${href ? 'is-clickable' : ''} ${className}`.trim()} tabIndex={href ? 0 : undefined} onClick={href ? () => { window.location.href = href; } : undefined} onKeyDown={href ? e => { if (e.key === 'Enter') window.location.href = href; } : undefined}>
        <div className="sk-row-main">{children}</div>
        {right != null && <div className="sk-row-side">{right}</div>}
    </div>
);

export const RowTitle = ({ href, children }) => (
    <a className="sk-title" href={href}>{children}</a>
);

export const RowMeta = ({ children, dim }) => (
    <div className={dim ? 'sk-meta sk-meta-dim' : 'sk-meta'}>{children}</div>
);

// Keeps a row's title and its metadata in one alignment column, so the text
// block starts at a single left edge instead of each line finding its own.
export const RowText = ({ children }) => <div className="sk-row-text">{children}</div>;

export const Toolbar = ({ children, right }) => (
    <div className="sk-toolbar">
        <div className="sk-toolbar-group">{children}</div>
        <div className="sk-toolbar-group">{right}</div>
    </div>
);

export const Select = ({ label, value, onChange, options }) => (
    <label className="sk-field">
        {label}
        <select className="sk-control" value={value} onChange={onChange}>
            <option value="all">All</option>
            {options.map((o, i) => (
                <option key={i} value={o}>{o}</option>
            ))}
        </select>
    </label>
);

export const Search = ({ value, onChange, placeholder = 'Search…' }) => (
    <input
        className="sk-control sk-search"
        type="search"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
    />
);

export const Empty = ({ children = 'Nothing here yet.' }) => (
    <div className="sk-state is-empty">{children}</div>
);

/** Shared shell for custom React Admin pages. Keeps page width and spacing
 * consistent without owning any routing or data behavior. */
export const PageShell = ({ children, className = '' }) => (
    <main className={`sk-page ${className}`.trim()}>{children}</main>
);

export const PageHeader = ({ eyebrow, title, description, actions }) => (
    <header className="sk-page__header">
        <div className="sk-page__heading">
            {eyebrow && <p className="sk-page__eyebrow">{eyebrow}</p>}
            <h1>{title}</h1>
            {description && <p className="sk-page__description">{description}</p>}
        </div>
        {actions && <div className="sk-page__actions">{actions}</div>}
    </header>
);

export const Surface = ({ children, title, description, className = '', actions }) => (
    <section className={`sk-surface ${className}`.trim()}>
        {(title || description || actions) && (
            <header className="sk-surface-header">
                <div className="sk-surface-heading">
                    {title && <h2>{title}</h2>}
                    {description && <p>{description}</p>}
                </div>
                {actions && <div className="sk-surface-actions">{actions}</div>}
            </header>
        )}
        <div className="sk-surface-body">{children}</div>
    </section>
);

export const MetricCard = ({ tone = 'green', label, value, detail }) => (
    <article className={`sk-metric-card ${tone}`}>
        <span className="sk-metric-label">{label}</span>
        <strong className="sk-metric-value">{value}</strong>
        {detail && <span className="sk-metric-detail">{detail}</span>}
    </article>
);

export const DetailGrid = ({ children, className = '' }) => <dl className={`sk-detail-grid ${className}`.trim()}>{children}</dl>;

export const DetailItem = ({ label, value, children, wide = false }) => (
    <div className={`sk-detail-item${wide ? ' is-wide' : ''}`}>
        <dt>{label}</dt>
        <dd className="sk-detail-value">{children != null ? children : (value || '—')}</dd>
    </div>
);

export const ProfileBand = ({ name, subtitle, avatar, status, children }) => (
    <header className="sk-profile-band">
        <div className="sk-profile-identity">{avatar}<div><h1>{name}</h1>{subtitle && <p>{subtitle}</p>}</div></div>
        <div className="sk-profile-state">{status}{children}</div>
    </header>
);

export const ActionButton = ({ children, variant = 'primary', className = '', ...props }) => (
    <button className={`sk-action sk-action-${variant} ${className}`.trim()} {...props}>{children}</button>
);

export const StatePanel = ({ children, state = 'empty', actions }) => {
    const role = state === 'loading' ? 'status' : state === 'error' ? 'alert' : undefined;
    return (
        <div className={`sk-state is-${state}`} role={role} aria-live={state === 'loading' ? 'polite' : undefined}>
            {children}
            {actions && <div className="sk-state__actions">{actions}</div>}
        </div>
    );
};
